import React from 'react';
import { Plus, Trash2, Clock } from 'lucide-react';

export interface DerouleLigne { heure: string; intitule: string }

/**
 * Éditeur de « déroulé » — une suite de moments horodatés, dans l'ordre.
 *
 * Répond à un besoin qu'aucun type de question existant ne couvrait : « c'est des heures précises, des fois
 * c'est accueil champagne, ils veulent que les convives se baladent et prennent des pièces à grignoter »
 * (Emeni, 2026-08-26). Ni une réponse libre ni un choix ne savent représenter une liste ordonnée qu'on
 * allonge ligne par ligne.
 *
 * ENREGISTRÉ EN TEXTE LISIBLE, une ligne par moment : « 18:00 — Accueil champagne ». C'est un choix
 * délibéré. Le moteur des Démarches stocke chaque réponse sous forme de texte, et ce texte ressort déjà tel
 * quel dans les emails, les récapitulatifs et les PDF. En sérialisant ainsi, le déroulé traverse toute la
 * chaîne existante sans qu'une seule de ces sorties ait à être modifiée — et reste lisible par un humain
 * qui regarderait la base directement, contrairement à un objet encodé.
 */
const SEPARATEUR = ' — ';

export const derouleVersTexte = (lignes: DerouleLigne[]): string =>
    lignes
        .filter((l) => l.heure.trim() || l.intitule.trim())
        .map((l) => `${l.heure.trim()}${SEPARATEUR}${l.intitule.trim()}`)
        .join('\n');

export const texteVersDeroule = (texte?: string | null): DerouleLigne[] => {
    if (!texte || !texte.trim()) return [];
    return texte.split('\n').filter((l) => l.trim()).map((ligne) => {
        const i = ligne.indexOf(SEPARATEUR);
        // Une ligne sans séparateur (saisie à la main, ou venue d'ailleurs) n'est pas perdue : elle devient
        // un intitulé sans heure plutôt que d'être ignorée.
        if (i === -1) return { heure: '', intitule: ligne.trim() };
        return { heure: ligne.slice(0, i).trim(), intitule: ligne.slice(i + SEPARATEUR.length).trim() };
    });
};

export function DerouleEditor({ valeur, onChange, lectureSeule }: {
    valeur: string;
    onChange: (texte: string) => void;
    lectureSeule?: boolean;
}) {
    const lignes = React.useMemo(() => texteVersDeroule(valeur), [valeur]);

    const maj = (suivantes: DerouleLigne[]) => onChange(derouleVersTexte(suivantes));
    const modifier = (i: number, patch: Partial<DerouleLigne>) =>
        maj(lignes.map((l, j) => (j === i ? { ...l, ...patch } : l)));
    const retirer = (i: number) => maj(lignes.filter((_, j) => j !== i));
    const ajouter = () => maj([...lignes, { heure: '', intitule: '' }]);

    if (lectureSeule) {
        if (lignes.length === 0) return <p className="text-[11px] text-content-muted italic">Aucun déroulé renseigné.</p>;
        return (
            <ol className="space-y-1">
                {lignes.map((l, i) => (
                    <li key={i} className="flex items-baseline gap-2 text-xs">
                        <span className="font-bold text-content w-12 shrink-0">{l.heure || '—'}</span>
                        <span className="text-content-muted">{l.intitule}</span>
                    </li>
                ))}
            </ol>
        );
    }

    return (
        <div className="space-y-1.5">
            {lignes.map((l, i) => (
                <div key={i} className="flex items-center gap-1.5">
                    <Clock size={12} className="text-content-muted shrink-0" />
                    <input type="time" value={l.heure} onChange={(e) => modifier(i, { heure: e.target.value })}
                        className="bg-surface border border-border rounded-lg px-2 py-1 text-xs text-content outline-none focus:border-brand-500 shrink-0" />
                    <input type="text" value={l.intitule} onChange={(e) => modifier(i, { intitule: e.target.value })}
                        placeholder="Ex : accueil champagne"
                        className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1 text-xs text-content outline-none focus:border-brand-500" />
                    <button type="button" onClick={() => retirer(i)} className="text-content-muted hover:text-red-400 shrink-0 p-1">
                        <Trash2 size={12} />
                    </button>
                </div>
            ))}
            <button type="button" onClick={ajouter}
                className="flex items-center gap-1 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                <Plus size={12} /> Ajouter un moment
            </button>
            {lignes.length === 0 && (
                <p className="text-[10px] text-content-muted italic">
                    Exemple : 18:00 accueil champagne, 19:00 pièces cocktail, 20:30 dîner assis.
                </p>
            )}
        </div>
    );
}
